// src/components/common/ChangeAwarePriceInput.tsx
import React, { forwardRef } from 'react';
import { ArrowUpRight, AlertCircle } from 'lucide-react';
import PriceInput from './PriceInput';

interface ChangeAwarePriceInputProps {
    value: number | null | undefined;
    previousValue?: number | null | undefined;
    label?: string;
    name?: string;
    onChange?: (value: number | null) => void;
    onBlur?: () => void;
    disabled?: boolean;
    readOnly?: boolean;
    showChange?: boolean;
    required?: boolean;
    error?: string;
    note?: string;
    className?: string;
    placeholder?: string;
    isReviewMode?: boolean;
}

const ChangeAwarePriceInput = forwardRef<HTMLInputElement, ChangeAwarePriceInputProps>(({
    value,
    previousValue,
    label,
    name,
    onChange,
    onBlur,
    disabled = false,
    readOnly = false,
    showChange = false,
    required = false,
    error,
    note,
    className = '',
    placeholder = '0.00',
    isReviewMode = false,
}, ref) => {
    const hasChanged = showChange && previousValue !== undefined && previousValue !== value;
    
    // Format price for display
    const formatPrice = (val: number | null | undefined): string => {
        if (val === null || val === undefined) return '';
        return val.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    return (
        <div className="space-y-1">
            <PriceInput
                ref={ref}  // ✅ Pass the ref to PriceInput
                value={value}
                onChange={onChange}
                label={label}
                name={name}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                readOnly={readOnly}
                error={error}
                onBlur={onBlur}
                className={className}
                isReviewMode={isReviewMode}
            />
            
            {hasChanged && (
                <div className="mt-1 p-2 bg-gray-50 rounded-lg border border-gray-200 flex items-start gap-2">
                    <ArrowUpRight className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500">Previous price</p>
                        <p className="text-sm text-gray-600 line-through">
                            ${formatPrice(previousValue)}
                        </p>
                    </div>
                </div>
            )}
            
            {note && !hasChanged && (
                <p className="text-xs text-gray-400 italic">{note}</p>
            )}
        </div>
    );
});

ChangeAwarePriceInput.displayName = 'ChangeAwarePriceInput';

export default ChangeAwarePriceInput;